/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Save } from 'lucide-react';
import { 
  getOrganizationSettings, 
  updateOrganizationSettingsCategory,
  syncOrgSettingsToPlatform 
} from '@shared/lib/api/organization_settings';
import {
  getMyOrganizations,
  getOrganizationMembers,
  setDefaultOrganization,
  updateOrganization,
} from '@shared/lib/api/user-organizations';
import { 
  VelocitySettings, 
  RepositorySettings, 
  AISettings,
  OrganizationIntegrationSettings,
  VertexAnthropicOrgSettings,
  GCPProjectOrgSettings,
  GCSIntegrationOrgSettings,
  DSAOrgSettings,
  SharePointOrgSettings,
  DEFAULT_ORGANIZATION_SETTINGS 
} from '@shared/types/organizationSettings';
import { useToast } from '../../hooks/use-toast';
import { getUserSettings } from '@shared/lib/api/user_settings';
import type { ProductAppSettings, SourceControlProvider } from '@shared/types/product-app-settings';
import type { UserOrganizationWithDetails } from '@shared/schema/organizations';

import {
  OrganizationInfoSection,
  GCPProjectSection,
  VertexAISection,
  GCSIntegrationSection,
  DSAOrgSection,
  SharePointSection,
  DisplaySettingsSection,
  VelocityBranchSection,
  RepositoryDefaultsSection,
  AISettingsSection,
} from './organization';

export function OrganizationSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Fetch user's organizations
  const { data: userOrgs = [], isLoading: isLoadingOrgs } = useQuery({
    ...getMyOrganizations(),
    staleTime: 1000 * 60 * 5,
  });
  
  // Find the default organization
  const defaultOrg = userOrgs.find((org: UserOrganizationWithDetails) => org.isDefault);
  
  // Fetch members of the default organization
  const { data: orgMembers = [], isLoading: isLoadingMembers } = useQuery({
    ...getOrganizationMembers(defaultOrg?.organizationId || ''),
    enabled: !!defaultOrg?.organizationId,
    staleTime: 1000 * 60 * 5,
  });
  
  // Mutation to change default organization
  const changeDefaultOrgMutation = useMutation({
    mutationFn: async (organizationId: string) => {
      const fn = setDefaultOrganization(organizationId);
      return fn(undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/me/organizations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/settings/organization'] });
      toast({
        title: 'Organization changed',
        description: 'Your default organization has been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to change organization',
        variant: 'destructive',
      });
    },
  });
  
  // Mutation to update organization name
  const updateOrgNameMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!defaultOrg?.organizationId) {
        throw new Error('No organization selected');
      }
      const fn = updateOrganization(defaultOrg.organizationId);
      return fn({ name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/me/organizations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/settings/organization'] });
      toast({
        title: 'Organization updated',
        description: 'The organization name has been updated.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update organization name',
        variant: 'destructive',
      });
    },
  });
  
  // Fetch organization settings
  const { data: orgSettings, isLoading, error } = useQuery(getOrganizationSettings());
  
  // Fetch user settings to get source control provider credentials
  const { data: userSettings } = useQuery({
    ...getUserSettings,
    staleTime: 1000 * 60 * 5,
  });
  
  // Extract source control provider info from user settings
  const productAppSettings = userSettings?.integrationSettings?.productApp as ProductAppSettings | undefined;
  const explicitProvider = productAppSettings?.sourceControlProvider;
  const inferredProvider: SourceControlProvider | null = 
    explicitProvider 
      ? explicitProvider 
      : productAppSettings?.githubConfig?.accessToken 
        ? 'github' 
        : productAppSettings?.gitlabConfig?.accessToken 
          ? 'gitlab' 
          : null;
  
  const gitCredentials = inferredProvider === 'github' 
    ? { 
        provider: 'github' as const, 
        accessToken: productAppSettings?.githubConfig?.accessToken || '',
      }
    : inferredProvider === 'gitlab'
    ? { 
        provider: 'gitlab' as const, 
        accessToken: productAppSettings?.gitlabConfig?.accessToken || '',
        instanceUrl: productAppSettings?.gitlabConfig?.instanceUrl || 'https://gitlab.com',
      }
    : null;
  
  // Local form state
  const [velocitySettings, setVelocitySettings] = useState<VelocitySettings>(
    DEFAULT_ORGANIZATION_SETTINGS.velocitySettings!
  );
  const [repositorySettings, setRepositorySettings] = useState<RepositorySettings>(
    DEFAULT_ORGANIZATION_SETTINGS.repositorySettings!
  );
  const [aiSettings, setAISettings] = useState<AISettings>(
    DEFAULT_ORGANIZATION_SETTINGS.aiSettings!
  );
  const [integrationSettings, setIntegrationSettings] = useState<OrganizationIntegrationSettings>(
    DEFAULT_ORGANIZATION_SETTINGS.integrationSettings || {}
  );
  const [gcpProjectSettings, setGCPProjectSettings] = useState<GCPProjectOrgSettings>({
    projectId: '',
    region: 'us-central1',
  });
  const [vertexAnthropicSettings, setVertexAnthropicSettings] = useState<VertexAnthropicOrgSettings>({
    enabled: false,
    projectId: '',
    region: 'global',
  });
  const [gcsSettings, setGCSSettings] = useState<GCSIntegrationOrgSettings>({
    enabled: false,
    projectId: '',
    bucketName: '',
    bucketPrefix: '',
  });
  const [dsaSettings, setDSASettings] = useState<DSAOrgSettings>({
    enabled: false,
    allowUserParticipation: true,
    velocityRepoUrl: undefined,
    defaultPollIntervalMs: 300000,
    defaultMinimumScore: 0.6,
  });
  const [sharePointSettings, setSharePointSettings] = useState<SharePointOrgSettings>({
    enabled: false,
    tenantId: '',
    clientId: '',
    clientSecret: '',
    siteUrl: '',
  });
  
  const [isDirty, setIsDirty] = useState(false);
  
  // Update local state when settings are fetched
  useEffect(() => {
    if (orgSettings) {
      if (orgSettings.velocitySettings) {
        setVelocitySettings(orgSettings.velocitySettings as VelocitySettings);
      }
      if (orgSettings.repositorySettings) {
        setRepositorySettings(orgSettings.repositorySettings as RepositorySettings);
      }
      if (orgSettings.aiSettings) {
        setAISettings(orgSettings.aiSettings as AISettings);
      }
      if (orgSettings.integrationSettings) {
        const intSettings = orgSettings.integrationSettings as OrganizationIntegrationSettings;
        setIntegrationSettings(intSettings);
        
        const sharedGcpProjectId = intSettings.gcpProject?.projectId || '';
        if (intSettings.gcpProject) {
          setGCPProjectSettings(intSettings.gcpProject);
        }
        
        if (intSettings.vertexAnthropic) {
          const vertexSettings = intSettings.vertexAnthropic;
          if (!vertexSettings.projectId && sharedGcpProjectId) {
            setVertexAnthropicSettings({
              ...vertexSettings,
              projectId: sharedGcpProjectId,
            });
          } else {
            setVertexAnthropicSettings(vertexSettings);
          }
        }
        
        if (intSettings.gcs) {
          const gcs = intSettings.gcs;
          if (!gcs.projectId && sharedGcpProjectId) {
            setGCSSettings({
              ...gcs,
              projectId: sharedGcpProjectId,
            });
          } else {
            setGCSSettings(gcs);
          }
        }
        
        if (intSettings.dsa) {
          setDSASettings(intSettings.dsa);
        }
        
        if (intSettings.sharepoint) {
          setSharePointSettings(intSettings.sharepoint);
        }
      }
    }
  }, [orgSettings]);
  
  // Mutations for updating settings
  const updateVelocityMutation = useMutation({
    mutationFn: async (settings: VelocitySettings) => {
      const fn = updateOrganizationSettingsCategory();
      return fn({ category: 'velocitySettings', settings });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/settings/organization'] });
      toast({
        title: 'Settings saved',
        description: 'Velocity branch settings have been updated.',
      });
      setIsDirty(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save settings',
        variant: 'destructive',
      });
    },
  });
  
  const updateRepoMutation = useMutation({
    mutationFn: async (settings: RepositorySettings) => {
      const fn = updateOrganizationSettingsCategory();
      return fn({ category: 'repositorySettings', settings });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/settings/organization'] });
      toast({
        title: 'Settings saved',
        description: 'Repository settings have been updated.',
      });
      setIsDirty(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save settings',
        variant: 'destructive',
      });
    },
  });
  
  const updateAIMutation = useMutation({
    mutationFn: async (settings: AISettings) => {
      const fn = updateOrganizationSettingsCategory();
      return fn({ category: 'aiSettings', settings });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/settings/organization'] });
      toast({
        title: 'Settings saved',
        description: 'AI settings have been updated.',
      });
      setIsDirty(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save settings',
        variant: 'destructive',
      });
    },
  });
  
  const updateIntegrationMutation = useMutation({
    mutationFn: async (settings: OrganizationIntegrationSettings) => {
      const fn = updateOrganizationSettingsCategory();
      return fn({ category: 'integrationSettings', settings });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['/api/settings/organization'] });
      
      try {
        const syncFn = syncOrgSettingsToPlatform();
        const syncResult = await syncFn(undefined);
        if (syncResult.synced && syncResult.synced.length > 0) {
          console.log('Synced org settings to platform:', syncResult.synced);
        }
      } catch (syncError) {
        console.warn('Failed to sync org settings to platform:', syncError);
      }
      
      toast({
        title: 'Settings saved',
        description: 'Integration settings have been updated and synced to platform.',
      });
      setIsDirty(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save settings',
        variant: 'destructive',
      });
    },
  });
  
  const handleSaveAll = async () => {
    const fullIntegrationSettings: OrganizationIntegrationSettings = {
      ...integrationSettings,
      gcpProject: gcpProjectSettings,
      vertexAnthropic: vertexAnthropicSettings,
      gcs: gcsSettings,
      dsa: dsaSettings,
      sharepoint: sharePointSettings,
    };
    
    await Promise.all([
      updateVelocityMutation.mutateAsync(velocitySettings),
      updateRepoMutation.mutateAsync(repositorySettings),
      updateAIMutation.mutateAsync(aiSettings),
      updateIntegrationMutation.mutateAsync(fullIntegrationSettings),
    ]);
  };
  
  const markDirty = () => setIsDirty(true);
  
  const isSaving = updateVelocityMutation.isPending || updateRepoMutation.isPending || 
                   updateAIMutation.isPending || updateIntegrationMutation.isPending;
  
  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <RefreshCw size={24} className="spin" style={{ color: '#6b7280' }} />
        <p style={{ marginTop: '12px', color: '#6b7280' }}>Loading organization settings...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        background: '#fef2f2', 
        border: '1px solid #fecaca',
        borderRadius: '8px',
        color: '#b91c1c' 
      }}>
        Failed to load organization settings. Please try again.
      </div>
    );
  }
  
  return (
    <div>
      <OrganizationInfoSection
        userOrgs={userOrgs}
        defaultOrg={defaultOrg}
        orgMembers={orgMembers}
        isLoadingOrgs={isLoadingOrgs}
        isLoadingMembers={isLoadingMembers}
        isChangingOrg={changeDefaultOrgMutation.isPending}
        onChangeOrganization={(orgId) => changeDefaultOrgMutation.mutate(orgId)}
        onUpdateOrganizationName={(name) => updateOrgNameMutation.mutate(name)}
        isUpdatingName={updateOrgNameMutation.isPending}
      />

      <GCPProjectSection
        settings={gcpProjectSettings}
        onChange={setGCPProjectSettings}
        onDirty={markDirty}
      />

      <VertexAISection
        settings={vertexAnthropicSettings}
        fallbackProjectId={gcpProjectSettings.projectId}
        onChange={setVertexAnthropicSettings}
        onDirty={markDirty}
      />

      <GCSIntegrationSection
        settings={gcsSettings}
        fallbackProjectId={gcpProjectSettings.projectId}
        onChange={setGCSSettings}
        onDirty={markDirty}
      />

      <SharePointSection
        settings={sharePointSettings}
        onChange={setSharePointSettings}
        onDirty={markDirty}
      />

      <DSAOrgSection
        settings={dsaSettings}
        gitCredentials={gitCredentials}
        organizationId={defaultOrg?.organizationId}
        organizationName={defaultOrg?.organization?.name}
        onChange={setDSASettings}
        onDirty={markDirty}
      />

      <DisplaySettingsSection
        showDashboard={velocitySettings.showDashboard ?? false}
        onChange={(showDashboard) => setVelocitySettings(prev => ({ ...prev, showDashboard }))}
        onDirty={markDirty}
      />

      <VelocityBranchSection
        settings={velocitySettings}
        onChange={setVelocitySettings}
        onDirty={markDirty}
      />

      <RepositoryDefaultsSection
        settings={repositorySettings}
        onChange={setRepositorySettings}
        onDirty={markDirty}
      />

      <AISettingsSection
        settings={aiSettings}
        onChange={setAISettings}
        onDirty={markDirty}
      />
      
      {/* Save Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        gap: '12px',
        padding: '16px 0',
        borderTop: '1px solid #e5e7eb'
      }}>
        {isDirty && (
          <span style={{ 
            fontSize: '13px', 
            color: '#f59e0b',
            display: 'flex',
            alignItems: 'center',
            marginRight: 'auto'
          }}>
            You have unsaved changes
          </span>
        )}
        <button
          className="btn"
          onClick={handleSaveAll}
          disabled={isSaving || !isDirty}
          style={{
            padding: '10px 24px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {isSaving ? (
            <>
              <RefreshCw size={16} className="spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} />
              Save Organization Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
}
